// Lecturas y escrituras de la sección Árboles. Mismas reglas que
// adminService: los errores se LANZAN con mensaje para la persona (nunca se
// vuelven "sin datos"), y si la tabla todavía no existe en la base, el mensaje
// lo dice ("falta aplicar la actualización").
//
// - cables_arboles: ficha. Admin y editor crean y editan; nadie borra, admin
//   da de baja con motivo.
// - cables_arbol_evaluaciones: append-only; admin anula con motivo.
// - cables_arboles_historial: la base guarda la fila anterior en cada cambio.
// - cables_v_arboles_estado: árbol + última evaluación vigente + riesgo,
//   próxima, vencida, sin_evaluacion y menciones de 30 días.
// - Archivos en el bucket cables-evidencia, carpeta arboles/<arbol_id>/.
import { supabase } from 'lib/supabase';
import { compressImage } from 'lib/compressImage';
import {
  BUCKET, errorAmigable, leer, leerPaginado, exigirMotivo, anular,
} from './adminService';

const ARBOLES = 'cables_arboles';
const EVALS = 'cables_arbol_evaluaciones';
const HIST = 'cables_arboles_historial';
const VISTA = 'cables_v_arboles_estado';
const INSPECCIONES = 'cables_inspecciones';
const MAX_PDF = 20 * 1024 * 1024;

const amigable = (texto) => {
  const e = new Error(texto);
  e.amigable = true;
  return e;
};

const nuevoId = () => (typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : (r % 4) + 8).toString(16);
  }));

// Lista con estado (vista), de todas las instalaciones; la pantalla filtra.
export const fetchArbolesEstado = () => leerPaginado(
  () => supabase.from(VISTA).select('*').order('codigo').order('id'),
  'No se pudieron cargar los árboles',
);

// Todas las evaluaciones (también anuladas; el reporte las descarta), para el
// resumen de control del reporte. Son pocas (cientos): se leen paginadas y el
// filtro por período e instalación se hace en memoria.
export const fetchEvaluacionesArboles = () => leerPaginado(
  () => supabase.from(EVALS)
    .select('id, arbol_id, fecha, tipo, evaluador_nombre, evaluador_profesion, riesgo, recomendaciones, acciones, proxima_evaluacion, informe_path, anulada, created_at')
    .order('fecha', { ascending: false }).order('id'),
  'No se pudieron cargar las evaluaciones de los árboles',
);

// Ficha de un árbol con sus evaluaciones (todas, también anuladas) y el
// historial de cambios. El historial es secundario: si falla, la ficha se
// muestra igual y avisa.
export async function fetchArbolFicha(id) {
  const contexto = 'No se pudo cargar el árbol';
  const [filas, evaluaciones] = await Promise.all([
    leer(() => supabase.from(ARBOLES).select('*').eq('id', id), contexto),
    leer(() => supabase.from(EVALS).select('*').eq('arbol_id', id)
      .order('fecha', { ascending: false }).order('created_at', { ascending: false }), 'No se pudieron cargar las evaluaciones'),
  ]);
  const arbol = filas[0] || null;
  if (!arbol) return { arbol: null, evaluaciones: [], historial: [], errorHistorial: null };
  let historial = [];
  let errorHistorial = null;
  try {
    historial = await leer(() => supabase.from(HIST).select('*').eq('arbol_id', id), 'No se pudo cargar el historial de cambios');
  } catch (e) {
    errorHistorial = e;
  }
  return { arbol, evaluaciones, historial, errorHistorial };
}

// Inspecciones que mencionan al árbol (detalles.estado_fitosanitario.arboles).
export const fetchMencionesArbol = (id) => leerPaginado(
  () => supabase.from(INSPECCIONES)
    .select('id, fecha, guia_nombre, resultados, detalles, anulada, instalacion')
    .filter('detalles->estado_fitosanitario->arboles', 'cs', JSON.stringify([id]))
    .order('fecha', { ascending: false }).order('id'),
  'No se pudieron cargar las menciones de la inspección diaria',
);

// Árboles por id (detalle de una inspección). Nunca lanza: devuelve null si
// no se pudo consultar, para mostrar "no se pudieron cargar".
export async function fetchArbolesPorIds(ids) {
  const unicos = [...new Set((ids || []).filter(Boolean))];
  if (!unicos.length) return [];
  try {
    const { data, error } = await supabase.from(ARBOLES)
      .select('id, codigo, especie, nombre_comun, sostiene, instalacion, activo').in('id', unicos);
    if (error) return null;
    return data || [];
  } catch {
    return null;
  }
}

const duplicado = (res, contexto) => {
  if (res?.error?.code === '23505') throw amigable(`${contexto}: ya existe un árbol con ese código.`);
};

export async function crearArbol(datos) {
  const contexto = 'No se pudo registrar el árbol';
  let res;
  try {
    res = await supabase.from(ARBOLES).insert({ ...datos, activo: true }).select('*');
  } catch (e) {
    throw errorAmigable(e, contexto);
  }
  duplicado(res, contexto);
  if (res?.error) throw errorAmigable(res.error, contexto);
  const fila = (res?.data || [])[0];
  if (!fila) throw errorAmigable({ code: '42501', message: 'permission' }, contexto);
  return fila;
}

// UPDATE que RLS no deja aplicar devuelve 0 filas sin error: se pide la fila.
export async function actualizarArbol(id, cambios, contexto = 'No se pudieron guardar los cambios') {
  let res;
  try {
    res = await supabase.from(ARBOLES).update(cambios).eq('id', id).select('*');
  } catch (e) {
    throw errorAmigable(e, contexto);
  }
  duplicado(res, contexto);
  if (res?.error) throw errorAmigable(res.error, contexto);
  const fila = (res?.data || [])[0];
  if (!fila) throw errorAmigable({ code: '42501', message: 'permission' }, contexto);
  return fila;
}

export async function darDeBajaArbol(id, motivo) {
  const m = exigirMotivo(motivo);
  return actualizarArbol(id, { activo: false, baja_motivo: m, baja_at: new Date().toISOString() }, 'No se pudo dar de baja el árbol');
}

async function subir(path, blob, contentType, contexto) {
  let res;
  try {
    res = await supabase.storage.from(BUCKET).upload(path, blob, { contentType, upsert: false });
  } catch (e) {
    throw errorAmigable(e, contexto);
  }
  if (res?.error) throw errorAmigable(res.error, contexto);
  return path;
}

// Foto de la ficha: se comprime, se sube a arboles/<id>/ y se guarda la ruta.
export async function subirFotoArbol(id, archivo) {
  const contexto = 'No se pudo subir la foto';
  if (!archivo || !/^image\//.test(archivo.type || '')) throw amigable(`${contexto}: elija una imagen.`);
  let blob = archivo;
  try { blob = (await compressImage(archivo)) || archivo; } catch { blob = archivo; }
  const path = await subir(`arboles/${id}/foto-${Date.now()}.jpg`, blob, 'image/jpeg', contexto);
  return actualizarArbol(id, { foto_path: path }, contexto);
}

export const validarInformePdf = (archivo) => {
  if (!archivo) return '';
  const esPdf = archivo.type === 'application/pdf' || /\.pdf$/i.test(archivo.name || '');
  if (!esPdf) return 'El informe debe ser un PDF.';
  if (archivo.size > MAX_PDF) return 'El PDF pesa más de 20 MB.';
  return '';
};

// Evaluación nueva. El id se genera aquí para que el PDF quede en
// arboles/<arbol_id>/eval-<id>.pdf ANTES de insertar (la evaluación no se
// edita después: no se puede agregar el informe con un UPDATE).
export async function crearEvaluacion(arbolId, datos, informe) {
  const contexto = 'No se pudo guardar la evaluación';
  const invalido = validarInformePdf(informe);
  if (invalido) throw amigable(`${contexto}: ${invalido}`);
  const id = nuevoId();
  let informePath = null;
  if (informe) {
    informePath = await subir(`arboles/${arbolId}/eval-${id}.pdf`, informe, 'application/pdf', 'No se pudo subir el informe');
  }
  let res;
  try {
    res = await supabase.from(EVALS).insert({ ...datos, id, arbol_id: arbolId, informe_path: informePath }).select('*');
  } catch (e) {
    throw errorAmigable(e, contexto);
  }
  if (res?.error) throw errorAmigable(res.error, contexto);
  return (res?.data || [])[0] || { ...datos, id, arbol_id: arbolId, informe_path: informePath, anulada: false };
}

export const anularEvaluacion = (id, motivo) => anular(EVALS, id, motivo, 'No se pudo anular la evaluación');

// URL firmada de un archivo (foto o informe). null si no se pudo.
export async function urlArchivo(path) {
  if (!path) return null;
  try {
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 3600);
    if (error) return null;
    return data?.signedUrl || null;
  } catch {
    return null;
  }
}
